import React, { useEffect, useState, useCallback } from 'react';
import { Table, Spinner, Alert, Form, Row, Col, Button, Pagination, Modal, Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import useAuthHeaders from '../../hooks/useAuthHeaders';

const API_BASE = process.env.REACT_APP_API_URL || 'https://localhost:5001/api/v1';
const PAGE_SIZE = 50;

const toDateInput = (d) => d.toISOString().slice(0, 10);

const today = new Date();
const defaultDateTo = toDateInput(today);
const defaultDateFrom = toDateInput(new Date(today.getFullYear(), today.getMonth(), today.getDate() - 6));

const EVENT_TYPE_OPTIONS = [
  '',
  'checkout.session.completed',
  'customer.created',
  'customer.updated',
  'customer.deleted',
  'customer.subscription.created',
  'customer.subscription.updated',
  'customer.subscription.deleted',
  'customer.subscription.trial_will_end',
  'invoice.created',
  'invoice.finalized',
  'invoice.paid',
  'invoice.payment_failed',
  'invoice.payment_succeeded',
  'invoice.upcoming',
  'payment_intent.succeeded',
  'payment_intent.payment_failed',
];

const formatDateTime = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('es-MX', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const truncate = (val, len = 14) => {
  if (!val) return '—';
  return val.length > len ? `${val.slice(0, len)}…` : val;
};

const statusVariant = (status) => {
  if (!status) return 'secondary';
  const s = status.toLowerCase();
  if (s === 'active' || s === 'trialing') return 'success';
  if (s === 'canceled' || s === 'past_due' || s === 'unpaid') return 'danger';
  if (s === 'incomplete' || s === 'incomplete_expired') return 'warning';
  return 'secondary';
};

const SortIcon = ({ col, sortBy, sortDir }) => {
  if (sortBy !== col) return <span style={{ opacity: 0.3, marginLeft: 4 }}>↕</span>;
  return <span style={{ marginLeft: 4 }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
};

const COLUMNS = [
  { key: 'receivedAt', label: 'Fecha', sortable: true },
  { key: 'type',       label: 'Tipo',   sortable: true },
  { key: 'customer',   label: 'Customer', sortable: false },
  { key: 'email',      label: 'Email',  sortable: false },
  { key: 'subscription', label: 'Suscripción', sortable: false },
  { key: 'actions',    label: '',       sortable: false },
];

const AdminStripeEvents = () => {
  const { getAuthHeaders } = useAuthHeaders();
  const navigate = useNavigate();

  const [dateFromInput, setDateFromInput] = useState(defaultDateFrom);
  const [dateToInput, setDateToInput] = useState(defaultDateTo);
  const [typeInput, setTypeInput] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [customerIdInput, setCustomerIdInput] = useState('');

  const [query, setQuery] = useState({
    dateFrom: defaultDateFrom,
    dateTo: defaultDateTo,
    type: '',
    email: '',
    stripeCustomerId: '',
    sortBy: 'receivedAt',
    sortDir: 'desc',
    page: 1,
    pageSize: PAGE_SIZE,
  });

  const [result, setResult] = useState({ items: [], totalCount: 0, page: 1, pageSize: PAGE_SIZE, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [detailEventId, setDetailEventId] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [copyState, setCopyState] = useState('idle');

  const fetchEvents = useCallback(async (q) => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const params = new URLSearchParams();
      if (q.dateFrom) params.set('dateFrom', q.dateFrom);
      if (q.dateTo)   params.set('dateTo', q.dateTo);
      if (q.type)     params.set('type', q.type);
      if (q.email)    params.set('email', q.email);
      if (q.stripeCustomerId) params.set('stripeCustomerId', q.stripeCustomerId);
      params.set('sortBy', q.sortBy);
      params.set('sortDir', q.sortDir);
      params.set('page', q.page);
      params.set('pageSize', q.pageSize);

      const response = await fetch(`${API_BASE}/admin/stripe-events?${params}`, { headers });
      if (!response.ok) throw new Error(`Error ${response.status}`);
      const data = await response.json();
      setResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchEvents(query);
  }, [query, fetchEvents]);

  const handleSearch = () => {
    setQuery(prev => ({
      ...prev,
      dateFrom: dateFromInput,
      dateTo: dateToInput,
      type: typeInput,
      email: emailInput.trim(),
      stripeCustomerId: customerIdInput.trim(),
      page: 1,
    }));
  };

  const handleClear = () => {
    setDateFromInput(defaultDateFrom);
    setDateToInput(defaultDateTo);
    setTypeInput('');
    setEmailInput('');
    setCustomerIdInput('');
    setQuery(prev => ({
      ...prev,
      dateFrom: defaultDateFrom,
      dateTo: defaultDateTo,
      type: '',
      email: '',
      stripeCustomerId: '',
      page: 1,
    }));
  };

  const handleSort = (col) => {
    const column = COLUMNS.find(c => c.key === col);
    if (!column?.sortable) return;
    setQuery(prev => ({
      ...prev,
      sortBy: col,
      sortDir: prev.sortBy === col && prev.sortDir === 'asc' ? 'desc' : 'asc',
      page: 1,
    }));
  };

  const handlePage = (p) => setQuery(prev => ({ ...prev, page: p }));

  const openDetail = async (eventId) => {
    setDetailEventId(eventId);
    setDetailData(null);
    setDetailError(null);
    setCopyState('idle');
    setDetailLoading(true);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE}/admin/stripe-events/${encodeURIComponent(eventId)}`, { headers });
      if (!response.ok) throw new Error(`Error ${response.status}`);
      const data = await response.json();
      setDetailData(data);
    } catch (err) {
      setDetailError(err.message);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDetailEventId(null);
    setDetailData(null);
    setDetailError(null);
    setCopyState('idle');
  };

  const handleCopy = async () => {
    if (!detailData?.rawJson) return;
    try {
      await navigator.clipboard.writeText(detailData.rawJson);
      setCopyState('copied');
      setTimeout(() => setCopyState('idle'), 1500);
    } catch {
      setCopyState('error');
      setTimeout(() => setCopyState('idle'), 1500);
    }
  };

  const formatRawJson = (raw) => {
    if (!raw) return '';
    try {
      return JSON.stringify(JSON.parse(raw), null, 2);
    } catch {
      return raw;
    }
  };

  const totalPages = result.totalPages ?? 0;
  const currentPage = result.page ?? 1;

  const buildPageItems = () => {
    const items = [];
    const delta = 2;
    const left = Math.max(1, currentPage - delta);
    const right = Math.min(totalPages, currentPage + delta);
    if (left > 1) {
      items.push(1);
      if (left > 2) items.push('...');
    }
    for (let i = left; i <= right; i++) items.push(i);
    if (right < totalPages) {
      if (right < totalPages - 1) items.push('...');
      items.push(totalPages);
    }
    return items;
  };

  return (
    <div className="status-container">
      <div className="page-header mb-4">
        <div>
          <Button
            variant="link"
            size="sm"
            className="p-0 mb-1 text-muted"
            onClick={() => navigate('/admin/reports')}
          >
            ← Reportes
          </Button>
          <h2 className="page-title">Eventos de Stripe</h2>
          <p className="page-subtitle text-muted">
            {result.totalCount} evento{result.totalCount !== 1 ? 's' : ''} encontrado{result.totalCount !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-3 p-3 border rounded bg-light">
        <Row className="g-2 align-items-end">
          <Col xs={6} md={2}>
            <Form.Label className="small mb-1">Fecha desde</Form.Label>
            <Form.Control
              size="sm"
              type="date"
              value={dateFromInput}
              onChange={e => setDateFromInput(e.target.value)}
            />
          </Col>
          <Col xs={6} md={2}>
            <Form.Label className="small mb-1">Fecha hasta</Form.Label>
            <Form.Control
              size="sm"
              type="date"
              value={dateToInput}
              onChange={e => setDateToInput(e.target.value)}
            />
          </Col>
          <Col xs={12} md={3}>
            <Form.Label className="small mb-1">Tipo de evento</Form.Label>
            <Form.Select
              size="sm"
              value={typeInput}
              onChange={e => setTypeInput(e.target.value)}
            >
              {EVENT_TYPE_OPTIONS.map(t => (
                <option key={t} value={t}>{t || 'Todos'}</option>
              ))}
            </Form.Select>
          </Col>
          <Col xs={6} md={2}>
            <Form.Label className="small mb-1">Email</Form.Label>
            <Form.Control
              size="sm"
              placeholder="cliente@..."
              value={emailInput}
              onChange={e => setEmailInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
            />
          </Col>
          <Col xs={6} md={2}>
            <Form.Label className="small mb-1">Customer ID</Form.Label>
            <Form.Control
              size="sm"
              placeholder="cus_..."
              value={customerIdInput}
              onChange={e => setCustomerIdInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
            />
          </Col>
          <Col xs={12} md={1} className="d-flex gap-1">
            <Button size="sm" variant="primary" onClick={handleSearch} className="w-100">
              Buscar
            </Button>
            <Button size="sm" variant="outline-secondary" onClick={handleClear} title="Limpiar">
              ✕
            </Button>
          </Col>
        </Row>
      </div>

      {error && <Alert variant="danger">Error al cargar eventos: {error}</Alert>}

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <Spinner animation="border" style={{ color: '#13a0ff' }} />
        </div>
      ) : (
        <>
          <div className="table-responsive">
            <Table hover size="sm" className="mb-2">
              <thead className="table-light">
                <tr>
                  {COLUMNS.map(({ key, label, sortable }) => (
                    <th
                      key={key}
                      onClick={() => handleSort(key)}
                      style={{
                        cursor: sortable ? 'pointer' : 'default',
                        whiteSpace: 'nowrap',
                        userSelect: 'none',
                      }}
                    >
                      {label}
                      {sortable && <SortIcon col={key} sortBy={query.sortBy} sortDir={query.sortDir} />}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {result.items.map((row) => (
                  <tr key={row.stripeEventId}>
                    <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(row.receivedAtUtc)}</td>
                    <td><code style={{ fontSize: '.8rem' }}>{row.type}</code></td>
                    <td title={row.stripeCustomerId ?? ''}>
                      <code style={{ fontSize: '.8rem' }}>{truncate(row.stripeCustomerId, 16)}</code>
                    </td>
                    <td>{row.userEmail ?? <span className="text-muted">—</span>}</td>
                    <td>
                      {row.subscriptionStatus ? (
                        <>
                          <Badge bg={statusVariant(row.subscriptionStatus)}>{row.subscriptionStatus}</Badge>
                          {row.planId != null && (
                            <span className="text-muted ms-2 small">Plan #{row.planId}</span>
                          )}
                        </>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td className="text-end">
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={() => openDetail(row.stripeEventId)}
                      >
                        Ver JSON
                      </Button>
                    </td>
                  </tr>
                ))}
                {result.items.length === 0 && (
                  <tr>
                    <td colSpan={COLUMNS.length} className="text-center text-muted py-4">
                      No se encontraron eventos con los filtros aplicados.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-2">
              <small className="text-muted">
                Página {currentPage} de {totalPages} ({result.totalCount} resultados)
              </small>
              <Pagination size="sm" className="mb-0">
                <Pagination.Prev disabled={currentPage === 1} onClick={() => handlePage(currentPage - 1)} />
                {buildPageItems().map((item, i) =>
                  item === '...'
                    ? <Pagination.Ellipsis key={`e${i}`} disabled />
                    : <Pagination.Item key={item} active={item === currentPage} onClick={() => handlePage(item)}>{item}</Pagination.Item>
                )}
                <Pagination.Next disabled={currentPage === totalPages} onClick={() => handlePage(currentPage + 1)} />
              </Pagination>
            </div>
          )}
        </>
      )}

      <Modal show={detailEventId != null} onHide={closeDetail} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1rem' }}>
            Detalle del evento
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detailLoading && (
            <div className="d-flex justify-content-center py-4">
              <Spinner animation="border" style={{ color: '#13a0ff' }} />
            </div>
          )}
          {detailError && <Alert variant="danger">Error: {detailError}</Alert>}
          {detailData && (
            <>
              <div className="mb-3 small">
                <div><strong>ID:</strong> <code>{detailData.stripeEventId}</code></div>
                <div><strong>Tipo:</strong> <code>{detailData.type}</code></div>
                <div><strong>Recibido:</strong> {formatDateTime(detailData.receivedAtUtc)}</div>
                {detailData.userEmail && <div><strong>Email:</strong> {detailData.userEmail}</div>}
                {detailData.stripeCustomerId && (
                  <div><strong>Customer:</strong> <code>{detailData.stripeCustomerId}</code></div>
                )}
                {detailData.stripeSubscriptionId && (
                  <div><strong>Subscription:</strong> <code>{detailData.stripeSubscriptionId}</code></div>
                )}
                {detailData.subscriptionStatus && (
                  <div>
                    <strong>Estado suscripción:</strong>{' '}
                    <Badge bg={statusVariant(detailData.subscriptionStatus)}>{detailData.subscriptionStatus}</Badge>
                    {detailData.planId != null && <span className="text-muted ms-2">Plan #{detailData.planId}</span>}
                  </div>
                )}
              </div>
              <pre
                style={{
                  maxHeight: '60vh',
                  overflow: 'auto',
                  fontSize: '.75rem',
                  background: '#f8f9fa',
                  padding: '.75rem',
                  borderRadius: 4,
                  border: '1px solid #dee2e6',
                  marginBottom: 0,
                }}
              >
                {formatRawJson(detailData.rawJson)}
              </pre>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={handleCopy}
            disabled={!detailData?.rawJson}
          >
            {copyState === 'copied' ? '¡Copiado!' : copyState === 'error' ? 'Error al copiar' : 'Copiar JSON'}
          </Button>
          <Button variant="secondary" size="sm" onClick={closeDetail}>Cerrar</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminStripeEvents;
